import { useLayoutEffect, useRef } from "react";
import { useAuth } from "./AuthProvider.js";
import { clearSession } from "./SessionStorage.js";
import { getItem } from "./LocalStorage.js";
import { isSessionEnded, getCurrentAccessToken, isAccessTokenValid } from "./refreshSession.js";

// Interceptors of the private client: token, language and app headers on the request; refresh on
// TOKEN_EXPIRED and session handling on the response. Mount it ONCE per app, near the root (inside
// the AuthProvider and the router): every request is then handled by a single interceptor pair.
// Registered in a layout effect, which runs before any useEffect of the tree: the first requests of
// the pages already go through it.
// Options (the app parts):
//   apiPrivate                private axios instance (createApiClients)
//   refresh, endSession       callbacks of useRefreshToken / useEndSession (stable)
//   getRequestHeaders()       extra request headers (e.g. the active winery)
//   onTokenExpired(error)     may take over an expired token (resolve true): no refresh then
//   onForbiddenRead(error)    a read (GET) denied by RBAC (e.g. show a toast)
//   onWineryRequired(error)   428: the request needs a winery context
const useApiInterceptors = ({
  apiPrivate,
  refresh,
  endSession,
  getRequestHeaders,
  onTokenExpired,
  onForbiddenRead,
  onWineryRequired,
}) => {
  const { auth } = useAuth();

  // Gli interceptor sono registrati una volta: tutto cio' che
  // leggono va preso da qui (aggiornato a ogni render), mai dalla closure,
  // altrimenti restano i valori del momento della registrazione (es. cantina
  // attiva assente -> 428 "Winery context required", o cantina vecchia).
  // refresh e endSession sono callback stabili.
  const latest = useRef({});
  latest.current = { auth, getRequestHeaders, onTokenExpired, onForbiddenRead, onWineryRequired };

  useLayoutEffect(() => {
    const requestIntercept = apiPrivate.interceptors.request.use(
      (config) => {
        const { auth, getRequestHeaders } = latest.current;
        if (!config.headers["Authorization"]) {
          // is the first attempt
          // token corrente (sincrono dopo un refresh) o, se non ancora impostato da setAuth, quello di auth
          config.headers["Authorization"] = `Bearer ${getCurrentAccessToken() ?? auth.accessToken}`; // taken from initial login
        }
        config.headers["Accept-Language"] = getItem("i18LanguageLocale")?.split("_")?.[0] || "it";
        Object.assign(config.headers, getRequestHeaders?.());

        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseIntercept = apiPrivate.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;

        // Errore gia' gestito (es. uscita dall'impersonazione), anche da un eventuale
        // altro interceptor sulla stessa istanza: non rielaborarlo, altrimenti il
        // ramo 401 sotto chiuderebbe la sessione appena ripristinata.
        if (error?.impersonationHandled) {
          return Promise.reject(error);
        }

        // Errore di /refresh: gia' gestito (chiusura sessione una sola volta)
        // da useRefreshToken, non va rielaborato da questo né dagli altri
        // interceptor registrati sulla stessa istanza.
        if (error?.isRefreshError) {
          return Promise.reject(error);
        }

        // 🔁 TOKEN SCADUTO → refresh
        if (
          error?.response?.status === 401 &&
          error?.response?.data?.error === "TOKEN_EXPIRED" &&
          !prevRequest?._retry
        ) {
          prevRequest._retry = true;

          // L'app puo' gestire il token scaduto in altro modo (es. fine impersonazione).
          if (await latest.current.onTokenExpired?.(error)) {
            return Promise.reject(error);
          }

          // Sessione gia' chiusa (refresh rifiutato): nessun altro /refresh.
          if (isSessionEnded()) {
            return Promise.reject(error);
          }

          // Il 401 e' arrivato dopo la fine di un refresh e la richiesta era
          // partita col token vecchio: il token e' gia' stato rinnovato, non si
          // ruota di nuovo. Si ripete UNA volta (_retry) col token corrente.
          const usedToken = String(prevRequest?.headers?.Authorization || "").replace(/^Bearer /, "");
          const currentToken = getCurrentAccessToken() ?? latest.current.auth?.accessToken;
          if (currentToken && usedToken !== currentToken && isAccessTokenValid(currentToken)) {
            prevRequest.headers.Authorization = `Bearer ${currentToken}`;
            return apiPrivate(prevRequest);
          }

          try {
            // refresh() e' single-flight: i 401 paralleli attendono la stessa promise
            const newAccessToken = await refresh();
            prevRequest.headers.Authorization = `Bearer ${newAccessToken}`;
            return apiPrivate(prevRequest);
          } catch (refreshError) {
            // la chiusura sessione e' gia' avvenuta in useRefreshToken
            return Promise.reject(refreshError);
          }
        }

        // 🚫 TOKEN INVALIDO → logout
        if (
          error?.response?.status === 403 &&
          error?.response?.data?.error === "INVALID_TOKEN"
        ) {
          endSession();
          return Promise.reject(error);
        }

        // ⛔ NON AUTORIZZATO (RBAC)
        if (error?.response?.status === 403) {
          // Le mutazioni mostrano gia' il proprio errore: qui solo le letture (GET)
          // negate, che altrimenti verrebbero inghiottite silenziosamente dai service.
          if ((prevRequest?.method || "get").toLowerCase() === "get") {
            latest.current.onForbiddenRead?.(error);
          }
          return Promise.reject({ ...error, isForbidden: true });
        }

        // 🚪 NON LOGGATO
        if (error?.response?.status === 401) {
          endSession();
          return Promise.reject(error);
        }

        // 🏭 winery non selezionata
        if (error?.response?.status === 428) {
          latest.current.onWineryRequired?.(error);
        }

        // Errori 4xx di business (es. validazione, operazione non completabile):
        // non invalidare la sessione, propaga l'errore al chiamante
        if (error?.response?.status >= 400 && error?.response?.status < 500) {
          return Promise.reject(error);
        }

        // Solo per errori gravi/inattesi (5xx, network errors): clear session
        clearSession("authToken");
        return Promise.reject(error);
      }
    );

    // clean up function
    return () => {
      apiPrivate.interceptors.request.eject(requestIntercept);
      apiPrivate.interceptors.response.eject(responseIntercept);
    };
  }, [apiPrivate, refresh, endSession]);
};

export default useApiInterceptors;
